import {RequiredLanguageProfile} from '../../../../types/shared-types';

export type DepartmentHealthDimension =
  | 'overall'
  | 'coreCompetencies'
  | 'hardSkills'
  | 'tools'
  | 'domainKnowledge'
  | 'softSkills'
  | 'responsibilities';

export const DEPARTMENT_HEALTH_DIMENSIONS: DepartmentHealthDimension[] = [
  'overall',
  'coreCompetencies',
  'hardSkills',
  'tools',
  'domainKnowledge',
  'softSkills',
  'responsibilities',
];

export const DEPARTMENT_HEALTH_NARRATIVE_DIMENSIONS = [
  'coreCompetencies',
  'hardSkills',
  'tools',
  'domainKnowledge',
  'softSkills',
  'responsibilities',
] as const;

export type DepartmentHealthNarrativeDimension = (typeof DEPARTMENT_HEALTH_NARRATIVE_DIMENSIONS)[number];

export type DepartmentHealthState = 'well_aligned' | 'functional' | 'at_risk';

export type DepartmentHealthConfidence = 'high' | 'medium' | 'low';

export type DepartmentHealthFreshnessBandKey = 'fresh' | 'ageing' | 'stale' | 'veryStale';

export type DepartmentHealthActionType =
  | 'skill_training'
  | 'single_point_of_failure'
  | 'redeployment_review'
  | 'development_plans'
  | 'data_quality'
  | 'all_clear';

export type DepartmentHealthActionPriority = 'high' | 'medium' | 'low';

export interface DepartmentHealthActionItem {
  actionType: DepartmentHealthActionType;
  priority: DepartmentHealthActionPriority | null;
  recommendation: RequiredLanguageProfile;
  expectedOutcome: RequiredLanguageProfile;
  profilePublicIds: string[];
  topCompetencies: RequiredLanguageProfile[];
  roleTitle: string | null;
  affectedProfileCount: number;
}

export interface DepartmentHealthDimensionScores {
  overall: number | null;
  coreCompetencies: number | null;
  hardSkills: number | null;
  tools: number | null;
  domainKnowledge: number | null;
  softSkills: number | null;
  responsibilities: number | null;
}

export interface DepartmentHealthScores {
  profileCount: number;
  departmentAverage: DepartmentHealthDimensionScores | null;
  profilesBelowLimit: number;
  profilesAtOrAboveLimit: number;
  lowestProfileScore: number | null;
  highestProfileScore: number | null;
}

export interface DepartmentHealthBenchmarks {
  mixedRoles: boolean;
  roleNorm: DepartmentHealthDimensionScores | null;
  companyAverage: DepartmentHealthDimensionScores;
  deltas: DepartmentHealthDimensionScores | null;
}

export interface DepartmentHealthNarrative {
  healthState: DepartmentHealthState;
}

export interface DepartmentHealthDataFreshness {
  freshCount: number;
  ageingCount: number;
  staleCount: number;
  veryStaleCount: number;
  freshPct: number;
  ageingPct: number;
  staleBandPct: number;
  veryStalePct: number;
  outdatedPct: number;
  confidence: DepartmentHealthConfidence;
}

export type DepartmentHealthProfilePath = 'develop' | 'redeploy';

export type DepartmentHealthEffortLevel = 'low' | 'moderate' | 'high';

export interface DepartmentHealthProfileSummary {
  profileId: string;
  publicId: string;
  targetProfileAlignmentScore: number | null;
  gapCount: number;
  path: DepartmentHealthProfilePath | null;
  effortLevel: DepartmentHealthEffortLevel | null;
}

export interface DepartmentHealthProfileDecisionGuide {
  mostUrgent: DepartmentHealthProfileSummary[];
  redeployCandidates: DepartmentHealthProfileSummary[];
  developCandidates: DepartmentHealthProfileSummary[];
  onTrack: DepartmentHealthProfileSummary[];
}

export interface DepartmentHealthSkillCoverageItem {
  skillName: RequiredLanguageProfile;
  coveragePct: number;
}

export interface DepartmentHealthSkillCoverage {
  critical: DepartmentHealthSkillCoverageItem[];
  partial: DepartmentHealthSkillCoverageItem[];
  full: DepartmentHealthSkillCoverageItem[];
}

export interface DepartmentHealthRoleCoverageItem {
  roleTitle: string;
  targetProfileId: string;
  profileCount: number;
  averageAlignmentScore: number | null;
  unmetCompetencies: RequiredLanguageProfile[];
}

export const DEPARTMENT_HEALTH_SKILL_COVERAGE_DISPLAY_LIMIT = 7;
export const DEPARTMENT_HEALTH_ROLE_UNMET_COMPETENCY_DISPLAY_LIMIT = 4;

export interface DepartmentHealthDetail {
  departmentId: string;
  scores: DepartmentHealthScores;
  benchmarks: DepartmentHealthBenchmarks;
  narrative: DepartmentHealthNarrative;
  actionItems: DepartmentHealthActionItem[];
  dataFreshness: DepartmentHealthDataFreshness;
  profileDecisionGuide: DepartmentHealthProfileDecisionGuide;
  skillCoverage: DepartmentHealthSkillCoverage;
  roleCoverage: DepartmentHealthRoleCoverageItem[];
}
